import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, ShieldCheckIcon, LockIcon } from 'lucide-react';
export function ProvisioningPaymentPage() {
  const [discountCode, setDiscountCode] = useState('LAUNCH50');
  const [discountApplied, setDiscountApplied] = useState(false);
  const [monitoringOptIn, setMonitoringOptIn] = useState(false);
  const navigate = useNavigate();
  const handleApplyDiscount = () => {
    if (discountCode.toUpperCase() === 'LAUNCH50') {
      setDiscountApplied(true);
    }
  };
  const setupFee = discountApplied ? 14.5 : 29.0;
  const total = setupFee + (monitoringOptIn ? 19.0 : 0);
  const handleProvision = () => {
    navigate('/setup/progress');
  };
  return <div className="min-h-screen bg-pk-surface py-12 px-4" data-id="element-1146">
      <div className="container mx-auto max-w-5xl" data-id="element-1147">
        <div className="mb-8" data-id="element-1148">
          <h1 className="text-3xl font-bold mb-2" data-id="element-1149">
            Provision your payments stack
          </h1>
          <p className="text-muted-foreground" data-id="element-1150">
            You're crossing the boundary from free recommendation to paid
            provisioning.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8" data-id="element-1151">
          {/* Left Column - What you get */}
          <div className="md:col-span-3 space-y-6" data-id="element-1152">
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm" data-id="element-1153">
              <h2 className="text-xl font-bold mb-4" data-id="element-1154">
                What PayKit will create
              </h2>
              <ul className="space-y-4" data-id="element-1155">
                <li className="flex items-start gap-3" data-id="element-1156">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-1157" />
                  <div data-id="element-1158">
                    <span className="font-medium block" data-id="element-1159">Products & Prices</span>
                    <span className="text-sm text-muted-foreground" data-id="element-1160">
                      3 products and 5 prices configured with correct billing
                      intervals.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1161">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-1162" />
                  <div data-id="element-1163">
                    <span className="font-medium block" data-id="element-1164">
                      Customer Portal Settings
                    </span>
                    <span className="text-sm text-muted-foreground" data-id="element-1165">
                      Configured to allow users to cancel or upgrade plans.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1166">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-1167" />
                  <div data-id="element-1168">
                    <span className="font-medium block" data-id="element-1169">Webhook Endpoint</span>
                    <span className="text-sm text-muted-foreground" data-id="element-1170">
                      1 endpoint listening to 6 essential events.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1171">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-1172" />
                  <div data-id="element-1173">
                    <span className="font-medium block" data-id="element-1174">
                      Implementation Pack
                    </span>
                    <span className="text-sm text-muted-foreground" data-id="element-1175">
                      Custom code snippets, env vars, and database schema.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-muted/50 border border-border rounded-xl p-6" data-id="element-1176">
              <h3 className="font-semibold mb-2 flex items-center gap-2" data-id="element-1177">
                <LockIcon className="w-4 h-4 text-muted-foreground" data-id="element-1178" />
                What PayKit can access
              </h3>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-1179">
                We use a restricted Stripe API key. We can read/write products,
                prices, and webhooks.
                <strong className="text-foreground" data-id="element-1180">
                  {' '}
                  We cannot access customer data, payment methods, or balances.
                </strong>
              </p>
            </div>
          </div>

          {/* Right Column - Payment */}
          <div className="md:col-span-2" data-id="element-1181">
            <div className="bg-card border border-border rounded-xl p-6 shadow-lg sticky top-24" data-id="element-1182">
              <h2 className="text-xl font-bold mb-6" data-id="element-1183">Order Summary</h2>

              <div className="space-y-4 mb-6 pb-6 border-b border-border" data-id="element-1184">
                <div className="flex justify-between items-center" data-id="element-1185">
                  <span className="text-muted-foreground" data-id="element-1186">
                    One-time Setup Fee
                  </span>
                  <div className="text-right" data-id="element-1187">
                    {discountApplied && <span className="text-sm text-muted-foreground line-through mr-2" data-id="element-1188">
                        $29.00
                      </span>}
                    <span className="font-medium" data-id="element-1189">${setupFee.toFixed(2)}</span>
                  </div>
                </div>

                {/* Discount Code */}
                <div className="flex gap-2" data-id="element-1190">
                  <input type="text" value={discountCode} onChange={e => setDiscountCode(e.target.value)} placeholder="Discount code" className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" disabled={discountApplied} data-id="element-1191" />
                  <button onClick={handleApplyDiscount} disabled={discountApplied || !discountCode} className="bg-muted text-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-muted/80 disabled:opacity-50" data-id="element-1192">
                    {discountApplied ? 'Applied' : 'Apply'}
                  </button>
                </div>
              </div>

              {/* Monitoring Add-on */}
              <div className="mb-6 pb-6 border-b border-border" data-id="element-1193">
                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1194">
                  <div className="mt-1" data-id="element-1195">
                    <input type="checkbox" checked={monitoringOptIn} onChange={e => setMonitoringOptIn(e.target.checked)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary" data-id="element-1196" />
                  </div>
                  <div data-id="element-1197">
                    <span className="font-medium block group-hover:text-primary transition-colors" data-id="element-1198">
                      Add Monitoring
                    </span>
                    <span className="text-sm text-muted-foreground block mb-1" data-id="element-1199">
                      Webhook health, drift alerts, update recommendations.
                    </span>
                    <span className="text-sm font-medium text-pk-info" data-id="element-1200">
                      $19/month
                    </span>
                  </div>
                </label>
              </div>

              <div className="flex justify-between items-end mb-8" data-id="element-1201">
                <span className="font-medium" data-id="element-1202">Total due today</span>
                <span className="text-3xl font-bold" data-id="element-1203">${total.toFixed(2)}</span>
              </div>

              <button onClick={handleProvision} className="w-full bg-primary text-primary-foreground hover:opacity-90 py-4 rounded-lg font-bold text-lg transition-all shadow-md flex items-center justify-center gap-2" data-id="element-1204">
                <ShieldCheckIcon className="w-5 h-5" data-id="element-1205" />
                Provision in Stripe
              </button>
              <p className="text-xs text-center text-muted-foreground mt-4" data-id="element-1206">
                By clicking provision, you agree to our Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}